import React, { JSX } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LzMCrud from './Crud';
import { lzFSortByPosition } from '@lz/services/Utils';
import { LzTThemeColor } from '@lz/app/Theming/types';
import { LzTOpenObject } from '@lz/typings/types';

export type LzTCrudListingActionIconGroup = {
  [key: string]: LzNCrudListingActionIcon;
};

export interface LzNCrudListingActionIcon {
  route?: string;
  routeParams?: LzTOpenObject;
  getRouteParams?: (record: any) => LzTOpenObject;
  title?: string;
  getTitle?: (record: any) => string;
  icon?: JSX.Element;
  getIcon?: (record: any) => JSX.Element | undefined;
  color?: LzTThemeColor;
  getColor?: (record: any) => string;
  hideOnDeleted?: boolean;
  showOnDeletedOnly?: boolean;
  hideOnCondition?: (record: any) => boolean;
  sortOrder?: number;
  showComponent?: (
    record: any,
    onHide?: () => void,
    callReload?: () => Promise<void>
  ) => any;
}

class LzMCrudListingActionIcon implements LzNCrudListingActionIcon {
  route: string;
  routeParams: LzTOpenObject;
  getRouteParams: (record: any) => LzTOpenObject;
  title: string;
  getTitle: (record: any) => string;
  icon?: JSX.Element;
  getIcon: (record: any) => JSX.Element | undefined;
  color: LzTThemeColor;
  getColor: (record: any) => string;
  hideOnDeleted: boolean;
  showOnDeletedOnly: boolean;
  hideOnCondition: (record: any) => boolean;
  sortOrder: number;
  showComponent?: (
    record: any,
    onHide?: () => void,
    callReload?: () => Promise<void>
  ) => any;

  constructor(model: LzNCrudListingActionIcon) {
    this.route = model.route || '';
    this.routeParams = model.routeParams || {};
    this.getRouteParams = model.getRouteParams
      ? model.getRouteParams
      : () => this.routeParams;
    this.title = model.title || '';
    this.getTitle = model.getTitle ? model.getTitle : () => this.title;
    this.icon = model.icon;
    this.getIcon = model.getIcon ? model.getIcon : () => this.icon;
    this.color = model.color || 'info';
    this.getColor = model.getColor ? model.getColor : () => this.color;
    this.hideOnDeleted = !!model.hideOnDeleted;
    this.showOnDeletedOnly = !!model.showOnDeletedOnly;
    this.hideOnCondition = model.hideOnCondition
      ? model.hideOnCondition
      : () => false;
    this.sortOrder = +(model.sortOrder || 1);
    this.showComponent = model.showComponent;
  }

  static parseData(data: any, crud: LzMCrud): LzTCrudListingActionIconGroup {
    const out: LzTCrudListingActionIconGroup = {};

    if (!data) {
      data = {};
    }

    const keys = Object.keys(data);
    for (const key of keys) {
      out[key] = new LzMCrudListingActionIcon(data[key]);
    }

    // Now add based on crud settings
    if (crud.isTreeBased && crud.showTreeAdd && !out.add) {
      out.add = new LzMCrudListingActionIcon({
        route: '{baseRoute}/add/{recordId}',
        title: 'Add Child',
        icon: <AddOutlinedIcon />,
        color: 'info',
        sortOrder: 5,
      });
    }
    if (crud.showView && !out.view) {
      out.view = new LzMCrudListingActionIcon({
        route: '{baseRoute}/{recordId}/view',
        title: 'View',
        icon: <VisibilityIcon />,
        color: 'info',
        sortOrder: 10,
      });
    }
    if (crud.showEdit && !out.edit) {
      out.edit = new LzMCrudListingActionIcon({
        route: '{baseRoute}/{recordId}/edit',
        title: 'Edit',
        icon: <EditIcon />,
        color: 'secondary',
        hideOnDeleted: true,
        sortOrder: 20,
      });
    }
    if (crud.showStatus && !out.status) {
      out.status = new LzMCrudListingActionIcon({
        getTitle: (rec) => (rec.is_active ? 'Active' : 'In-Active'),
        getIcon: (rec) => (rec.is_active ? <CheckIcon /> : <CloseIcon />),
        getColor: (rec) => (rec.is_active ? 'success' : 'warning'),
        hideOnDeleted: true,
        sortOrder: 30,
      });
    }
    if (crud.showDelete && !out.delete) {
      out.delete = new LzMCrudListingActionIcon({
        title: 'Delete',
        icon: <DeleteIcon />,
        color: 'error',
        hideOnDeleted: true,
        sortOrder: 40,
      });
    }
    if (crud.allowDeletedRestore && !out.deleted_restore) {
      out.deleted_restore = new LzMCrudListingActionIcon({
        title: 'Restore',
        icon: <RestoreFromTrashIcon />,
        color: 'success',
        showOnDeletedOnly: true,
        sortOrder: 50,
      });
    }
    if (crud.allowDeletedPurge && !out.deleted_purge) {
      out.deleted_purge = new LzMCrudListingActionIcon({
        title: 'Delete Forever',
        icon: <DeleteForeverIcon />,
        color: 'error',
        showOnDeletedOnly: true,
        sortOrder: 60,
      });
    }

    // Sort by sort order and return
    return lzFSortByPosition(out, 'sortOrder');
  }
}

export default LzMCrudListingActionIcon;
